import React, {type ComponentType, useEffect, useMemo, useState} from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import {useSelector} from 'react-redux';
import styled from 'styled-components';

import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {StyledSidebarH5} from 'app/general/bootstrap/utils';
import {
  useInfiniteListFetcherRTK,
  UseQueryType,
} from 'app/general/datafetchers/hooks/useInfiniteListFetcherRTK';
import {SearchInputField} from 'app/general/widgets/SearchInputField';
import StateButton from 'app/general/widgets/StateButton';
import {setSidebarListState} from 'app/procedures/actions';
import {useModelChangeSubscription} from 'app/subscriptions';
import {SORT_BY_TYPES} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import DashboardSidebarCollapsableListItem from './DashboardSidebarCollapsableListItem';

import type {PermissionCodeType} from 'app/general/types';

interface Props<
  IT extends {id: number; html_url?: string},
  FP extends {page: string}
> {
  ItemComponent?: ComponentType<any>;
  icon: string;
  title: string;
  reduxKey: string;
  showSearch: boolean;
  searchPlaceholder?: string;
  noResultsMessage: JSX.Element | string;
  canCreate?: boolean;
  createLink?: string;
  itemProps?: Record<string, unknown>;
  RelatedLink?: ComponentType<any>;
  itemNameProcessor: (item: any) => string | JSX.Element;
  watchModelChange: string;
  fetchParams?:
    | {
        permission: PermissionCodeType;
      }
    | Record<string, never>;
  useDataHook: UseQueryType<FP, IT>;
  paramsConverter: (query: Record<string, any>) => FP;
  pageParam?: string;
}

type Result = {
  isLoading: boolean;
  isFetching: boolean;
  hasNext: boolean;
  resultsLength: number;
};

const DEFAULT_RESULTS = {
  isLoading: true,
  isFetching: true,
  hasNext: false,
  resultsLength: 0,
};
export function DashboardSidebarCollapsableList<
  IT extends {id: number; html_url?: string},
  FP extends {page: string}
>({
  icon,
  title,
  showSearch,
  searchPlaceholder,
  ItemComponent = DashboardSidebarCollapsableListItem,
  itemNameProcessor,
  canCreate,
  createLink,
  RelatedLink,
  noResultsMessage,
  watchModelChange,
  useDataHook,
  paramsConverter,
  pageParam,
  itemProps,
  fetchParams = {},
  reduxKey,
}: Props<IT, FP>) {
  const [pages, setPages] = useState<Array<number>>([]);
  const [resultsPages, setResultsPages] = useState<Record<number, Result>>({
    1: DEFAULT_RESULTS,
  });
  const actions = useAsyncActions({setSidebarListState});

  const {open, searchQuery} = useSelector((state) => {
    let sidebarState = state.proceduresPage.getIn([
      'dashboardSidebar',
      reduxKey,
    ]);
    sidebarState = sidebarState != null ? sidebarState.toJS() : {};
    return {
      open: false,
      searchQuery: '',
      ...sidebarState,
    };
  });

  useEffect(() => {
    // reset the pages when open/close is toggled.
    setResultsPages(open ? {1: DEFAULT_RESULTS} : {});
    setPages(open ? [1] : []);
  }, [open]);

  // True if any of our pages are loading.
  const isLoading = useMemo(
    () =>
      Object.values(resultsPages).some((result) => result.isLoading === true),
    [resultsPages]
  );
  const resultsLength = useMemo(
    () =>
      Object.values(resultsPages).reduce(
        (total, result) => total + result.resultsLength,
        0
      ),
    [resultsPages]
  );
  const hasNext = useMemo(
    () => resultsPages[pages.length]?.hasNext,
    [resultsPages]
  );
  const isFetching = useMemo(
    () =>
      Object.values(resultsPages).some((result) => result.isFetching === true),
    [resultsPages]
  );

  const openStateIcon = !open
    ? 'fa fa-chevron-right'
    : isLoading
    ? 'fa fa-circle-notch fa-spin'
    : 'fa fa-chevron-down';

  const loadMore = () => setPages((prev) => [...prev, prev.length + 1]);

  const toggleOpen = () => {
    const currentOpen = !open;
    setReduxState({
      open: currentOpen,
    });
  };

  const updateSearchLoadItems = (searchQuery: string) => {
    setReduxState({
      searchQuery,
    });
  };

  const setReduxState = (state: Record<string, unknown>) => {
    actions.setSidebarListState(reduxKey, state);
  };

  return (
    <StyledContainerDiv className={open ? 'open' : ''}>
      <StyledSidebarH5 className="d-flex align-items-center">
        <a className="title text-truncate" onClick={toggleOpen}>
          <i className={`toggle far fa-fw ${openStateIcon} mr-2`} />
          <i className={`d-md-none d-lg-inline fa ${icon} mr-1`} /> {title}
        </a>
        {canCreate && createLink != null ? (
          <FullHTMLLink
            className="btn btn-link text-secondary btn-sm ml-auto"
            title={gt('Create')}
            html_url={createLink}
          >
            <i className="fa fa-plus-circle" />
          </FullHTMLLink>
        ) : null}
        {RelatedLink != null ? <RelatedLink /> : null}
      </StyledSidebarH5>
      {open ? (
        <div className="">
          {showSearch ? (
            <SearchInputField
              initialValue={searchQuery}
              onChange={updateSearchLoadItems}
              placeholder={searchPlaceholder != null ? searchPlaceholder : ''}
            />
          ) : null}
          <ul className="list-unstyled pb-3 mb-0">
            {!isLoading && resultsLength === 0 ? (
              <li className="no-results">{noResultsMessage}</li>
            ) : null}

            {pages.map((page) => (
              <Page
                key={page}
                page={page}
                pageParam={pageParam}
                setResults={(results) =>
                  setResultsPages((prev) => ({...prev, [page]: results}))
                }
                onRemovePage={() => {
                  setPages((prev) => prev.filter((p) => p !== page));
                  setResultsPages((prev) => {
                    delete prev[page];
                    return prev;
                  });
                }}
                open={open}
                watchModelChange={watchModelChange}
                useDataHook={useDataHook}
                fetchParams={fetchParams}
                searchQuery={searchQuery}
                paramsConverter={paramsConverter}
                ItemComponent={ItemComponent}
                itemNameProcessor={itemNameProcessor}
                itemProps={itemProps}
              />
            ))}
            {!isLoading && (hasNext || isFetching) ? (
              <li>
                <StateButton
                  isIdle={!isFetching}
                  className="load-more mt-3 btn btn-sm btn-outline-primary"
                  busyText={gt('Loading...')}
                  onClick={loadMore}
                >
                  {gt('Load more')}
                </StateButton>
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </StyledContainerDiv>
  );
}

type PageProps<FP, IT> = {
  page: number;
  fetchParams:
    | {
        permission: PermissionCodeType;
      }
    | Record<string, never>;

  searchQuery: string;
  open: boolean;
  setResults: (Result) => unknown;
  onRemovePage: () => unknown;
  watchModelChange: string;
  ItemComponent: ComponentType<any>;
  itemNameProcessor: (item: any) => string | JSX.Element;
  itemProps?: Record<string, unknown>;
  useDataHook: UseQueryType<FP, IT>;
  paramsConverter: (query: Record<string, any>) => FP;
  pageParam?: string;
};

const Page = <
  FP extends {page: string},
  IT extends {id: number; html_url?: string}
>({
  page,
  fetchParams,
  searchQuery,
  open,
  setResults,
  onRemovePage,
  watchModelChange,
  useDataHook,
  paramsConverter,
  pageParam,
  ItemComponent,
  itemNameProcessor,
  itemProps,
}: PageProps<FP, IT>) => {
  const {isFetching, isLoading, results, refetch, hasNext, error} =
    useInfiniteListFetcherRTK({
      fetchParams: {
        ...fetchParams,
        search: searchQuery,
        ordering: SORT_BY_TYPES.ALPHABETHICALLY,
      },
      page,
      useDataHook,
      paramsConverter,
      pageParam,
    });

  useEffect(() => {
    if (error?.status === 404) {
      onRemovePage();
    }
  }, [error]);

  useEffect(() => {
    setResults({
      isFetching,
      isLoading,
      hasNext,
      resultsLength: results?.length ?? 0,
    });
  }, [isLoading, isFetching, hasNext, results?.length]);

  useModelChangeSubscription(
    (message, {runWhenAnyModelMatch}) => {
      if (open && watchModelChange != null && refetch != null) {
        runWhenAnyModelMatch(message, watchModelChange, refetch);
      }
    },
    [open, watchModelChange]
  );
  return (
    <>
      {results.map((item) => (
        <ItemComponent
          key={item.html_url || item.id}
          nameProcessor={itemNameProcessor}
          item={item}
          {...itemProps}
        />
      ))}
    </>
  );
};

const StyledContainerDiv = styled.div.attrs<{className?: string}>({
  className: 'my-4',
})`
  ul {
    li:not(.tags) {
      margin: 0 !important;
      font-size: 90%;

      a {
        padding: 5px 0;
        display: block;
      }
    }

    li.no-results {
      color: ${colors.mediumGray};
    }
  }

  input[type='search'] {
    font-size: 90%;
  }

  .load-more {
    font-size: 80%;
  }
`;
